import type { ComponentType, ReactNode } from "react";
import type * as THREE from "three";
import { ThemeTokens, type ThemeTokensInput } from "@cfg/theme";
import { z } from "zod";

/* -------------------------------------------------------------------------- */
/* Serializable brand config: lives in brand.config.ts and is emitted as       */
/* brand.json at deploy so tokens/copy can change without a rebuild.          */
/* -------------------------------------------------------------------------- */

export const BrandFeatures = z.object({
  saveConfigs: z.boolean().default(true),
  shareLinks: z.boolean().default(true),
  export3mf: z.boolean().default(true),
  exportStl: z.boolean().default(true),
  /** Downloads need a signed-in user (only where sign-in is available: no `auth` and no dev user leaves export open). */
  exportRequiresSignIn: z.boolean().default(false),
  floatingLabels: z.boolean().default(true),
  analytics: z.boolean().default(true),
  /** Show designs published to the `beta` channel for signed-in staff. */
  betaChannel: z.boolean().default(false),
  /** Let users tag saved configurations with what they fit (see `fitTags`). */
  fitTags: z.boolean().default(false),
  /** Community gallery: users publish configurations, everyone browses them. */
  publicGallery: z.boolean().default(false),
});
export type BrandFeatures = z.infer<typeof BrandFeatures>;

export const BrandCatalog = z.object({
  /** Design ids or globs ("brickbrackets.*") to include; empty = every design in the listed packs. */
  include: z.array(z.string()).default([]),
  exclude: z.array(z.string()).default([]),
  /** Only designs carrying at least one of these tags. */
  tags: z.array(z.string()).default([]),
});

/** How a brand names and validates fit tags. The core only knows "something this fits". */
export const BrandFitTags = z.object({
  /** Noun for one tag, e.g. "LEGO® set". */
  label: z.string().default("Fits"),
  /** Stricter client-side check on the ref, e.g. "^\d{3,7}(-\d{1,2})?$". The API only enforces a generic shape. */
  refPattern: z.string().optional(),
  refPlaceholder: z.string().default(""),
  namePlaceholder: z.string().default("Name (optional)"),
  /** Shown in the footer and on community pages, e.g. a trademark notice. */
  disclaimer: z.string().optional(),
  /** Appended to a ref typed without a "-" suffix, e.g. "-1" so 42115 becomes 42115-1 (LEGO set variants). */
  refSuffix: z.string().optional(),
  /** External catalog searched while typing a ref (signed-in users; the API holds the key). Only "rebrickable" (LEGO sets) exists. */
  lookup: z.enum(["rebrickable"]).optional(),
  /** Picture of a ref without a lookup (signed-out visitors), `{ref}` substituted, e.g. "https://cdn.example/sets/{ref}.jpg". */
  imageUrl: z.string().optional(),
  /** Refs and names the home page's set search types out while idle ("42115-1", "Millennium Falcon"); defaults to the most-tagged refs. */
  examples: z.array(z.string()).default([]),
});
export type BrandFitTags = z.infer<typeof BrandFitTags>;

/**
 * Document head for the brand's site: favicons, web manifest and social cards.
 * Paths are site-relative and point into the brand's `public/` folder
 * (`brands/<id>/public`), which the shell serves at the site root in dev and
 * copies into the build for that brand only. Read at build time (the tags are
 * written into index.html), so deploy-time brand.json overrides don't apply.
 */
export const BrandMeta = z.object({
  /** Meta and social description. Defaults to `tagline`. */
  description: z.string().optional(),
  /** Browser UI colour. Defaults to `theme.colors.bg`. */
  themeColor: z.string().optional(),
  icons: z
    .object({
      /** Multi-size .ico, e.g. "/favicon.ico". */
      ico: z.string().optional(),
      /** Scalable icon, e.g. "/icon.svg". */
      svg: z.string().optional(),
      /** 180x180 PNG, e.g. "/apple-touch-icon.png". */
      appleTouch: z.string().optional(),
    })
    .default({}),
  /** Web app manifest, e.g. "/site.webmanifest". */
  manifest: z.string().optional(),
  /** Open Graph / Twitter card image (1200x630 recommended). Made absolute with `domain` when site-relative. */
  ogImage: z.object({ url: z.string(), width: z.number().int().positive().optional(), height: z.number().int().positive().optional(), alt: z.string().optional() }).optional(),
});
export type BrandMeta = z.infer<typeof BrandMeta>;

/**
 * Who runs the site and its legal pages. `privacy` and `terms` are Markdown
 * (headings `#`/`##`, paragraphs, `- ` lists, `**bold**`, `[links](https://…)`)
 * rendered at /privacy and /terms; `attribution` is one Markdown line in the
 * footer (e.g. the data source). Translations go in `i18n[locale].legal`.
 */
export const BrandLegal = z.object({
  /** Person or company responsible for the site, as named in the legal pages. */
  operator: z.string().optional(),
  /** Footer "Contact" (mailto) and the address the legal pages give. */
  contactEmail: z.string().optional(),
  privacy: z.string().optional(),
  terms: z.string().optional(),
  attribution: z.string().optional(),
});
export type BrandLegal = z.infer<typeof BrandLegal>;

/** A brand's own text in one locale. Every field is optional; empty strings mean "not translated yet". */
export const BrandLocaleText = z.object({
  /** Copy overrides for this locale (same keys as `copy`). */
  copy: z.record(z.string(), z.string()).default({}),
  tagline: z.string().optional(),
  fitTags: z
    .object({ label: z.string().optional(), refPlaceholder: z.string().optional(), namePlaceholder: z.string().optional(), disclaimer: z.string().optional() })
    .prefault({}),
  meta: z.object({ description: z.string().optional() }).prefault({}),
  legal: z.object({ privacy: z.string().optional(), terms: z.string().optional(), attribution: z.string().optional() }).prefault({}),
});
export type BrandLocaleText = z.infer<typeof BrandLocaleText>;
export type BrandLocaleTextInput = z.input<typeof BrandLocaleText>;

export const BrandConfig = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  name: z.string(),
  tagline: z.string().default(""),
  domain: z.string().optional(),
  /** Default locale: the language `copy`, `tagline`, `fitTags` and `meta` are written in. */
  locale: z.string().default("en"),
  /** Locales offered in the language switcher (BCP 47, e.g. ["en", "es-MX"]). Empty or one entry = default only, no switcher. */
  locales: z.array(z.string()).default([]),
  /** Brand text per non-default locale (see `BrandLocaleText`). Empty strings fall back. */
  i18n: z.record(z.string(), BrandLocaleText).default({}),
  theme: ThemeTokens,
  /** Scene preset id (must exist in the brand's `scenes` map). */
  scene: z.string().default("default"),
  /** Pack pins, e.g. "brickbrackets@^1". */
  packs: z.array(z.string().regex(/^[a-z0-9-]+(@.+)?$/)),
  catalog: BrandCatalog.prefault({}),
  features: BrandFeatures.prefault({}),
  fitTags: BrandFitTags.prefault({}),
  /** UI copy overrides keyed by string id (see `copyKeys`). */
  copy: z.record(z.string(), z.string()).default({}),
  /** Where the shell fetches the registry. Defaults to the shared API. */
  apiBaseUrl: z.string().optional(),
  auth: z
    .object({
      authority: z.string(),
      clientId: z.string(),
      /** Cognito hosted-UI domain, e.g. https://auth.example.com */
      domain: z.string().optional(),
    })
    .optional(),
  analyticsKey: z.string().optional(),
  /** Favicons, manifest and social card (see `BrandMeta`). Optional; without it the site has none. */
  meta: BrandMeta.optional(),
  /** Footer links and the legal pages (see `BrandLegal`). Optional; without it the footer has no links. */
  legal: BrandLegal.optional(),
});
export type BrandConfig = z.infer<typeof BrandConfig>;
export type BrandConfigInput = z.input<typeof BrandConfig>;

/* -------------------------------------------------------------------------- */
/* Scene preset: how the model is lit and shaded. Code, not JSON.             */
/* -------------------------------------------------------------------------- */

export interface MaterialContext {
  three: typeof THREE;
  /** Brand model colour token, already resolved. */
  color: string;
  /** Preview vs export-quality mesh (export meshes render the same way). */
  lod: "preview" | "export";
}

export interface LightSpec {
  intensity: number;
  position: [number, number, number];
  color?: string;
  castShadow?: boolean;
}

/** An emissive panel in the environment map. Positions are world space (Z up); only direction and size matter. */
export interface LightformerSpec {
  form?: "rect" | "circle" | "ring";
  intensity: number;
  color?: string;
  position: [number, number, number];
  /** Uniform size, or [width, height]. */
  scale: number | [number, number];
  /** Point the panel faces. Default the origin. */
  target?: [number, number, number];
}

export interface ScenePreset {
  id: string;
  /** Build the model material. Return any three.js material, including ShaderMaterial with custom GLSL. */
  material: (ctx: MaterialContext) => THREE.Material;
  environment?: {
    /** drei Environment preset name, or an HDRI url. */
    preset?: "studio" | "city" | "warehouse" | "apartment" | "sunset" | "dawn" | "night" | "forest" | "lobby" | "park";
    hdri?: string;
    /** Reflection/ambient strength of the environment map. Default 1. */
    intensity?: number;
    /** Draw the environment as background too. */
    background?: boolean;
    /**
     * Procedural studio: emissive panels rendered into the environment map
     * (on top of `preset`/`hdri` when given, alone otherwise). No network
     * fetch, and the panels show up as crisp highlights on glossy materials.
     */
    lightformers?: LightformerSpec[];
  };
  lights: {
    ambient?: number;
    hemisphere?: { sky: string; ground: string; intensity: number };
    key?: LightSpec;
    fill?: LightSpec;
    rim?: LightSpec;
  };
  /** CSS colour, "transparent", or a two-stop vertical gradient. */
  background: string | { gradient: [string, string] };
  grid?: {
    show: boolean;
    cellSize?: number;
    sectionSize?: number;
    color?: string;
    sectionColor?: string;
    fadeDistance?: number;
    infinite?: boolean;
  };
  shadows?: boolean;
  toneMapping?: "ACES" | "Neutral" | "None";
  exposure?: number;
  camera?: {
    fov?: number;
    /** Multiplier on the auto-fit distance. */
    distanceFactor?: number;
    /** Auto-rotate speed in the catalog cards and idle viewport; 0 disables. */
    autoRotate?: number;
  };
  /** Optional post-processing hook: receives the R3F root state; return cleanup. */
  effects?: ComponentType;
}

/* -------------------------------------------------------------------------- */
/* Slot components and widget overrides                                        */
/* -------------------------------------------------------------------------- */

export interface CatalogEntryView {
  id: string;
  version: string;
  title: string;
  description?: string;
  tags: string[];
  thumbnailUrl?: string;
  href: string;
  /**
   * What the card shows. Absent or "design" for catalog cards; "community" for
   * community entries (tagged parameter sets), which brands should render without `version`.
   */
  kind?: "design" | "community";
  /** Community cards: the design this configuration is made from (display title). */
  designTitle?: string;
  /** Community cards: display names of the people who tagged it (nobody owns an entry). */
  taggers?: string[];
  /** Community cards: what it fits (e.g. LEGO set ref + name), for fit chips. `description` still carries a joined summary. */
  fits?: { ref: string; name?: string }[];
  /** Community cards: noun for one fit tag (brand `fitTags.label`, e.g. "LEGO® set"). */
  fitLabel?: string;
  /** Community cards: how many people registered a print of it. */
  printerCount?: number;
  /** ISO timestamp of the last update (community cards). */
  updatedAt?: string;
  /** Distinct signed-in users who downloaded it (design cards; absent when unknown). */
  downloads?: number;
  /** Design cards standing for a family: its variants' names ("Wall", "SKÅDIS"); the card opens the first. */
  variants?: string[];
}

export interface FooterProps {
  brand: BrandConfig;
  /** Resolved `footer.madeWith` copy; render it so brands keep one source of truth for this line. */
  madeWith?: string;
  /** Privacy, Terms, Contact… from `legal` (labels resolved); render them as a link row. */
  links?: { label: string; href: string }[];
  /** `legal.attribution`, rendered (inline links included). */
  attribution?: ReactNode;
}

export interface HeaderProps {
  brand: BrandConfig;
  user?: { name?: string; email?: string; picture?: string } | null;
  onSignIn?: () => void;
  onSignOut?: () => void;
  nav: { label: string; href: string; active?: boolean }[];
  /**
   * Language switcher state. `options` holds every locale the brand offers
   * (native `label`s); render a switcher only when there is more than one.
   */
  locale?: { current: string; options: { code: string; label: string }[]; onChange: (code: string) => void };
}

export interface BrandSlots {
  Header: ComponentType<HeaderProps>;
  Footer: ComponentType<FooterProps>;
  CatalogCard: ComponentType<{ entry: CatalogEntryView; children?: ReactNode }>;
  EmptyState: ComponentType<{ title: string; body?: string; action?: ReactNode }>;
  Logo: ComponentType<{ brand: BrandConfig; size?: number }>;
  /** Wraps the configurator page; brands use it to add side content around the panel. */
  ConfiguratorFrame: ComponentType<{ children: ReactNode; aside?: ReactNode }>;
}

export interface ParamWidgetProps<T = unknown> {
  spec: {
    name: string;
    label: string;
    type: "number" | "int" | "bool" | "select" | "string";
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    options?: { value: string; label: string }[];
    description?: string;
  };
  value: T;
  onChange: (next: T) => void;
  disabled?: boolean;
  invalid?: string;
}
export type ParamWidget<T = unknown> = ComponentType<ParamWidgetProps<T>>;

/* -------------------------------------------------------------------------- */
/* The full brand object apps import through the @brand alias                 */
/* -------------------------------------------------------------------------- */

export interface Brand {
  config: BrandConfig;
  scenes: Record<string, ScenePreset>;
  slots?: Partial<BrandSlots>;
  widgets?: Record<string, ParamWidget>;
  /** Extra CSS appended after the tokens (overrides.css contents). */
  css?: string;
}

export interface BrandInput {
  config: BrandConfigInput;
  scenes: Record<string, ScenePreset>;
  slots?: Partial<BrandSlots>;
  widgets?: Record<string, ParamWidget>;
  css?: string;
}

export function defineBrand(input: BrandInput): Brand {
  const config = BrandConfig.parse(input.config);
  if (!input.scenes[config.scene]) {
    throw new Error(`Brand ${config.id}: scene preset "${config.scene}" is not defined (have: ${Object.keys(input.scenes).join(", ")})`);
  }
  return { config, scenes: input.scenes, slots: input.slots, widgets: input.widgets, css: input.css };
}

/** Deep-merge serializable overrides (brand.json) into a compiled brand config. */
export function mergeBrandConfig(base: BrandConfig, overrides: Partial<BrandConfigInput> | null | undefined): BrandConfig {
  if (!overrides) return base;
  const merged: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(overrides)) {
    if (v === undefined) continue;
    const cur = (merged as Record<string, unknown>)[k];
    if (v && typeof v === "object" && !Array.isArray(v) && cur && typeof cur === "object" && !Array.isArray(cur)) {
      merged[k] = deepMerge(cur as Record<string, unknown>, v as Record<string, unknown>);
    } else {
      merged[k] = v;
    }
  }
  return BrandConfig.parse(merged);
}

function deepMerge(a: Record<string, unknown>, b: Record<string, unknown>): Record<string, unknown> {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) {
    if (v && typeof v === "object" && !Array.isArray(v) && a[k] && typeof a[k] === "object" && !Array.isArray(a[k])) {
      out[k] = deepMerge(a[k] as Record<string, unknown>, v as Record<string, unknown>);
    } else if (v !== undefined) {
      out[k] = v;
    }
  }
  return out;
}

export * from "./copy.ts";
export * from "./i18n.ts";

export type { ThemeTokensInput };
